import {
  useCallback, useEffect, useId, useRef,
  type KeyboardEvent, type ReactNode, type RefObject,
} from 'react';
import { createPortal } from 'react-dom';
import { Check } from '../../../icons';
import { useAnchoredMenu } from '../../../hooks/useAnchoredMenu';
import './DropdownMenu.css';

/**
 * DropdownMenu — 단추에 붙는 작은 메뉴 (디자인 정본 F3 · F4 · 2026-09-15 첫 공통 코드).
 *
 * 쓰는 곳: 게시판 정렬 고르개(최신순 · 조회순 · 댓글순) · 카드 머리 「⋯」. 지금까지는
 * `useAnchoredMenu` 훅만 공통이고 메뉴는 화면마다 손으로 그렸다(정본이 9곳을 적어 뒀다).
 *
 * 정본 규약:
 *   ★ 항목 높이 28 · **파괴적 항목은 맨 아래, 구분선 위에서 갈라 놓는다**(`danger` 를 주면 알아서 내린다).
 *   ★★ **`createPortal(document.body)`** — 레일 · 목록이 `overflow:hidden` 이라 안에서 열면 잘린다
 *      (채팅 첨부 메뉴 · 저장 메뉴가 실제로 잘렸다). 자리는 `useAnchoredMenu` 가 버튼을 재서 정한다.
 *   ★ 포커스 트랩을 걸지 **않는다**(모달이 아니다 — ADR). Esc 로 닫고 **트리거로 포커스를 돌려준다**.
 *     바깥을 누르면 닫힌다 · Tab 으로 나가면 닫힌다.
 *   ★ 트리거에 `aria-haspopup="menu"` · `aria-expanded` — `renderTrigger` 가 받는 값을 그대로 펼친다.
 *
 * `checkable` 을 주면 항목이 `menuitemradio` 가 되고, 열 때 **고른 항목에 포커스**가 간다(정렬 고르개).
 */

export const DROPDOWN_ITEM_HEIGHT = 28;
const MENU_PADDING = 4;
const SEPARATOR_HEIGHT = 9;

export interface DropdownMenuItem {
  id: string;
  label: ReactNode;
  /** 오른쪽 작은 글자(단축키 등). */
  shortcut?: string;
  /** 되돌릴 수 없는 일 — 맨 아래 구분선 밑으로 간다. */
  danger?: boolean;
  disabled?: boolean;
  /** `checkable` 메뉴에서 지금 고른 항목. */
  checked?: boolean;
  onSelect: () => void;
}

export interface DropdownMenuTriggerProps {
  ref: RefObject<HTMLButtonElement>;
  'aria-haspopup': 'menu';
  'aria-expanded': boolean;
  'aria-controls': string | undefined;
  onClick: () => void;
  onKeyDown: (e: KeyboardEvent<HTMLElement>) => void;
}

export interface DropdownMenuProps {
  /** 메뉴 이름(스크린리더) — 「정렬」. */
  label: string;
  items: readonly DropdownMenuItem[];
  /** 트리거를 그린다. 받은 값을 `<Button {...p}>` 처럼 그대로 펼친다. */
  renderTrigger: (props: DropdownMenuTriggerProps) => ReactNode;
  /** 가로 맞춤 — 기본 `end`(버튼 오른쪽 끝). */
  align?: 'start' | 'end';
  /** 메뉴 폭(px). 넘침 판정에도 쓴다. */
  width?: number;
  checkable?: boolean;
}

/** 파괴적 항목을 뒤로 뺀다 — 순수 함수. */
export function splitMenuItems(items: readonly DropdownMenuItem[]) {
  return {
    regular: items.filter((it) => !it.danger),
    danger: items.filter((it) => it.danger),
  };
}

/** 좌표를 미리 재려고 메뉴 높이를 계산한다(CSS 와 함께 고친다). */
export function menuHeight(items: readonly DropdownMenuItem[]): number {
  const { regular, danger } = splitMenuItems(items);
  const sep = regular.length > 0 && danger.length > 0 ? SEPARATOR_HEIGHT : 0;
  return MENU_PADDING * 2 + items.length * DROPDOWN_ITEM_HEIGHT + sep;
}

export function DropdownMenu({
  label, items, renderTrigger, align = 'end', width = 200, checkable = false,
}: DropdownMenuProps) {
  const menuId = useId();
  const menu = useAnchoredMenu<HTMLButtonElement>({ width, height: menuHeight(items) }, { align });
  const { anchorRef, close, open, toggle } = menu;
  const listRef = useRef<HTMLDivElement>(null);
  const { regular, danger } = splitMenuItems(items);

  const focusables = useCallback(
    () => Array.from(
      listRef.current?.querySelectorAll<HTMLButtonElement>('[data-menu-item]:not([aria-disabled="true"])') ?? [],
    ),
    [],
  );

  const closeAndReturn = useCallback(() => {
    close();
    anchorRef.current?.focus();
  }, [close, anchorRef]);

  // 열리면 고른 항목(없으면 첫 항목)으로 포커스.
  useEffect(() => {
    if (!open) return;
    const list = focusables();
    const checked = list.find((el) => el.getAttribute('aria-checked') === 'true');
    (checked ?? list[0])?.focus();
  }, [open, focusables]);

  // 바깥을 누르면 닫는다. 트리거를 누른 것은 트리거의 onClick 이 처리한다(두 번 토글되지 않게).
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (listRef.current?.contains(target) || anchorRef.current?.contains(target)) return;
      close();
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open, close, anchorRef]);

  const onTriggerKeyDown = (e: KeyboardEvent<HTMLElement>) => {
    if (!open && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
      e.preventDefault();
      toggle();
    }
  };

  const onListKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const list = focusables();
    const idx = list.indexOf(document.activeElement as HTMLButtonElement);
    const move = (next: number) => {
      e.preventDefault();
      if (list.length === 0) return;
      list[(next + list.length) % list.length]?.focus();
    };
    switch (e.key) {
      case 'ArrowDown': move(idx + 1); break;
      case 'ArrowUp': move(idx < 0 ? list.length - 1 : idx - 1); break;
      case 'Home': move(0); break;
      case 'End': move(list.length - 1); break;
      case 'Escape': e.preventDefault(); closeAndReturn(); break;
      case 'Tab': close(); break;
      default: break;
    }
  };

  const select = (item: DropdownMenuItem) => {
    if (item.disabled) return;
    closeAndReturn();
    item.onSelect();
  };

  const renderItem = (item: DropdownMenuItem) => (
    <button
      key={item.id}
      type="button"
      data-menu-item=""
      role={checkable ? 'menuitemradio' : 'menuitem'}
      aria-checked={checkable ? !!item.checked : undefined}
      aria-disabled={item.disabled || undefined}
      tabIndex={-1}
      className={[
        'wc-dropdown__item',
        item.danger ? 'wc-dropdown__item--danger' : '',
        item.disabled ? 'wc-dropdown__item--disabled' : '',
      ].filter(Boolean).join(' ')}
      onClick={() => select(item)}
    >
      {checkable && (
        <span className="wc-dropdown__check" aria-hidden>
          {item.checked && <Check />}
        </span>
      )}
      <span className="wc-dropdown__label">{item.label}</span>
      {item.shortcut && <kbd className="wc-dropdown__shortcut">{item.shortcut}</kbd>}
    </button>
  );

  return (
    <>
      {renderTrigger({
        ref: anchorRef,
        'aria-haspopup': 'menu',
        'aria-expanded': open,
        'aria-controls': open ? menuId : undefined,
        onClick: toggle,
        onKeyDown: onTriggerKeyDown,
      })}
      {menu.pos && createPortal(
        <div
          ref={listRef}
          id={menuId}
          role="menu"
          aria-label={label}
          className="wc-dropdown"
          style={{ top: menu.pos.top, left: menu.pos.left, inlineSize: width }}
          onKeyDown={onListKeyDown}
        >
          {regular.map(renderItem)}
          {regular.length > 0 && danger.length > 0 && <div role="separator" className="wc-dropdown__sep" />}
          {danger.map(renderItem)}
        </div>,
        document.body,
      )}
    </>
  );
}
